
/**
 * 安全地解析 JSON 字符串，如果解析失败则返回默认值。
 *
 * @param text - 要解析的 JSON 字符串。
 * @param defaultValue - 解析失败时返回的默认值，默认为空对象。
 * @param reviver - 可选的函数，用于转换结果。该函数会为对象的每个成员调用。
 * @returns 解析后的对象，如果解析失败则返回默认值。
 */
export const JSONParse = (text: string, defaultValue: object = {}, reviver?: Parameters<typeof JSON.parse>[1]) => {
  try {
    return JSON.parse(text, reviver);
  } catch (error) {
    console.error(error);
    return defaultValue;
  }
};

type StringifyParams = Parameters<typeof JSON.stringify>;

/**
 * 将给定的值转换为 JSON 字符串。
 *
 * @param value - 要转换的值。
 * @param defaultValue - 如果转换失败，返回的默认值，默认为空字符串。
 * @param replacer - 可选的替换函数或数组，用于选择或修改要包含在 JSON 字符串中的属性。
 * @param space - 可选的用于增加返回值 JSON 字符串的可读性的空白字符。
 * @returns 转换后的 JSON 字符串，如果转换失败则返回默认值。
 */
export const JSONStringify = (value: unknown, defaultValue = '', replacer?: StringifyParams[1], space?: StringifyParams[2]) => {
  if (!value || typeof value !== 'object') {
    return defaultValue;
  }
  try {
    return JSON.stringify(value, replacer, space);
  } catch (error) {
    console.error(error);
    return defaultValue;
  }
};

/**
 * 有序对象类型，使用 Map 来保持 key 的顺序
 */
export type OrderedObject = Map<string, unknown>;

/**
 * 解析 JSON 字符串并保持对象 key 的顺序。
 *
 * 与标准的 JSON.parse 不同，此方法返回的对象会保持 JSON 字符串中 key 的原始顺序。
 * 对于嵌套对象，所有层级的 key 顺序都会被保持。
 *
 * @param text - 要解析的 JSON 字符串
 * @param defaultValue - 解析失败时返回的默认值，默认为空 Map
 * @returns 解析后的有序对象（使用 Map 表示），如果解析失败则返回默认值
 *
 * @example
 * ```typescript
 * const jsonStr = '{"c": 3, "a": 1, "b": 2}';
 * const ordered = JSONParseOrdered(jsonStr);
 *
 * // 遍历时会保持原始顺序：c, a, b
 * for (const [key, value] of ordered) {
 *   console.log(key, value); // 输出: c 3, a 1, b 2
 * }
 *
 * // 转换为普通对象（会丢失顺序保证）
 * const plainObj = Object.fromEntries(ordered);
 * ```
 *
 * @remarks
 * - 返回的对象是 Map 类型，需要使用 Map 的方法来访问数据
 * - 如果需要转换为普通对象，可以使用 Object.fromEntries()，但会丢失顺序保证
 * - 数组类型会保持为普通数组
 * - 基本类型（string, number, boolean, null）保持不变
 */
export const JSONParseOrdered = (text: string, defaultValue: OrderedObject = new Map()): OrderedObject | unknown => {
  try {
    return parseOrderedValue(JSON.parse(text));
  } catch (error) {
    console.error('JSONParseOrdered 解析失败:', error);
    return defaultValue;
  }
};

/**
 * 递归解析值，将对象转换为有序 Map
 *
 * @param value - 要转换的值
 * @returns 转换后的值，对象会被转换为 Map
 */
function parseOrderedValue(value: unknown): unknown {
  if (value === null || typeof value !== 'object') {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(parseOrderedValue);
  }

  // 对于普通对象，转换为 Map 以保持顺序
  const orderedMap = new Map<string, unknown>();
  const obj = value as Record<string, unknown>;
  for (const key of Object.keys(obj)) {
    orderedMap.set(key, parseOrderedValue(obj[key]));
  }

  return orderedMap;
}

/**
 * 将有序对象转换回 JSON 字符串，保持 key 的顺序
 *
 * @param orderedObj - 有序对象（Map 类型）
 * @param space - 可选的用于增加返回值 JSON 字符串的可读性的空白字符
 * @returns JSON 字符串
 *
 * @example
 * ```typescript
 * const ordered = new Map([['c', 3], ['a', 1], ['b', 2]]);
 * const jsonStr = orderedStringify(ordered);
 * console.log(jsonStr); // '{"c":3,"a":1,"b":2}'
 * ```
 */
export const orderedStringify = (orderedObj: unknown, space?: string | number): string => JSON.stringify(orderedObj, (_key, value) => {
  if (value instanceof Map) {
    const obj: Record<string, unknown> = {};
    for (const [k, v] of value) {
      obj[k] = v;
    }
    return obj;
  }
  return value;
}, space);
